/**
 * KickReasonModal — форма исключения участника мастером, CORE-137.
 *
 * Причина (radio) + необязательное описание + Отмена/Исключить. Модал не
 * знает, куда уходит кик (REST у эфиров, SignalR у комнат) — наверх отдаёт
 * только { reason, description } через onConfirm и ждёт промис, чтобы
 * показать спиннер и ошибку внутри формы. Успешный confirm закрывает модал
 * снаружи (родитель сбрасывает target).
 *
 * Причины совпадают по ключам с ReportReason («harassment» и т.д.) — БЭ
 * складывает их в лог user_kicked_from_room и письмо на
 * moderation@agapella.com, поэтому ключи меняются только вместе с БЭ.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, TextInput, Modal, ScrollView, ActivityIndicator, KeyboardAvoidingView, Platform } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../context/ThemeContext';
import { friendlyApiError } from '../services/errorMessages';
import { kickSubmitDisabled, resolveKickSubmit } from '../utils/kickSubmitGate';
import type { ReportReason } from '../types/reports';
import { fontWeights } from '../styles/typographyTokens';

import { ACCENT_TEXT } from '../styles/accentText';
export interface KickTarget {
  userId: string;
  displayName: string;
}

export interface KickPayload {
  reason: ReportReason;
  description?: string;
}

interface KickReasonModalProps {
  /** Кого исключаем. `null` → модал скрыт. */
  target: KickTarget | null;
  /** Пояснение под заголовком — что произойдёт после исключения. */
  subtitle?: string;
  onClose: () => void;
  /** Родитель выполняет сам кик; reject оставляет модал открытым с ошибкой. */
  onConfirm: (payload: KickPayload) => Promise<void>;
}

const REASON_OPTIONS: ReadonlyArray<{ key: ReportReason; label: string }> = [
  { key: 'spam', label: 'Спам' },
  { key: 'harassment', label: 'Оскорбления или травля' },
  { key: 'fraud', label: 'Мошенничество' },
  { key: 'inappropriate_content', label: 'Неприемлемый контент' },
  { key: 'other', label: 'Другое' },
];

export const KickReasonModal: React.FC<KickReasonModalProps> = ({ target, subtitle, onClose, onConfirm }) => {
  const { colors: tc, isDark } = useTheme();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CORE-1297 — «нажал ИСКЛЮЧИТЬ без причины» перестаёт быть тишиной. Разбор —
  // в шапке `kickSubmitGate`.
  const [reasonMissing, setReasonMissing] = useState(false);

  // Сброс формы при каждом новом открытии — исключение другого участника
  // не должно наследовать прошлый выбор.
  useEffect(() => {
    if (target) {
      setReason(null);
      setDescription('');
      setError(null);
      setReasonMissing(false);
      setIsSending(false);
    }
  }, [target]);

  if (!target) return null;

  const submit = async () => {
    const action = resolveKickSubmit({ reason, isSending });
    if (action === 'ignore') return;
    if (action === 'ask-reason') {
      setReasonMissing(true);
      return;
    }
    setIsSending(true);
    setError(null);
    setReasonMissing(false);
    try {
      await onConfirm({ reason: reason!, description: description.trim() || undefined });
    } catch (err) {
      setError(friendlyApiError(err));
      setIsSending(false);
    }
  };

  return (
    <Modal transparent visible animationType="fade" onRequestClose={onClose}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1 }}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: 16 } as any}>
          <View style={{ width: '100%', maxWidth: 440, borderRadius: 20, backgroundColor: isDark ? '#15171A' : '#FFFFFF', overflow: 'hidden' } as any}>
            <View style={{ padding: 20, paddingBottom: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderBottomWidth: 1, borderBottomColor: tc.border } as any}>
              <View style={{ flex: 1, paddingRight: 12 }}>
                <Text style={{ color: tc.textPrimary, fontSize: 18, fontWeight: fontWeights.bold } as any} numberOfLines={1}>
                  {`Исключить ${target.displayName}?`}
                </Text>
                {subtitle ? (
                  <Text style={{ color: tc.textMuted, fontSize: 12, marginTop: 4 } as any}>{subtitle}</Text>
                ) : null}
              </View>
              <TouchableOpacity onPress={onClose} style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6', alignItems: 'center', justifyContent: 'center' }}>
                <Ionicons name="close" size={18} color={tc.textMuted} />
              </TouchableOpacity>
            </View>

            <ScrollView style={{ maxHeight: 480 }} keyboardShouldPersistTaps="handled">
              <View style={{ padding: 20, gap: 16 }}>
                <View>
                  <Text style={{ color: tc.textMuted, fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 1, marginBottom: 8 } as any}>ПРИЧИНА</Text>
                  <View style={{ gap: 8 }}>
                    {REASON_OPTIONS.map(opt => {
                      const active = reason === opt.key;
                      return (
                        <TouchableOpacity
                          key={opt.key}
                          onPress={() => { setReason(opt.key); setReasonMissing(false); }}
                          activeOpacity={0.85}
                          style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 12, borderWidth: 1, borderColor: active ? '#E98245' : tc.border, backgroundColor: active ? (isDark ? 'rgba(233, 130, 69,0.1)' : '#FFF5EE') : 'transparent' } as any}
                        >
                          <Ionicons
                            name={active ? 'radio-button-on' : 'radio-button-off'}
                            size={18}
                            color={active ? ACCENT_TEXT : tc.textMuted}
                          />
                          <Text style={{ color: tc.textPrimary, fontSize: 14, fontWeight: active ? '700' : '500' } as any}>{opt.label}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                  {reasonMissing ? (
                    <Text
                      testID="kick-reason-required"
                      style={{ color: '#EF4444', fontSize: 13, marginTop: 10 } as any}
                    >
                      Выберите причину — она уходит в лог модерации.
                    </Text>
                  ) : null}
                </View>

                <View>
                  <Text style={{ color: tc.textMuted, fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 1, marginBottom: 8 } as any}>ОПИСАНИЕ</Text>
                  <TextInput
                    value={description}
                    onChangeText={setDescription}
                    multiline
                    placeholder="Расскажите, что произошло (необязательно)"
                    placeholderTextColor={tc.textMuted}
                    style={{ borderWidth: 1, borderColor: tc.border, borderRadius: 12, padding: 14, color: tc.textPrimary, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#FFFFFF', fontSize: 14, minHeight: 90, textAlignVertical: 'top' } as any}
                  />
                </View>

                {error ? (
                  <Text style={{ color: '#EF4444', fontSize: 13 } as any}>{error}</Text>
                ) : null}
              </View>
            </ScrollView>

            <View style={{ padding: 20, paddingTop: 14, flexDirection: 'row', gap: 10, justifyContent: 'flex-end', borderTopWidth: 1, borderTopColor: tc.border } as any}>
              <TouchableOpacity
                onPress={onClose}
                disabled={isSending}
                style={{ paddingHorizontal: 18, paddingVertical: 12, borderRadius: 100, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6' } as any}
              >
                <Text style={{ color: tc.textPrimary, fontSize: 13, fontWeight: '600' } as any}>Отмена</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={submit}
                testID="kick-confirm"
                disabled={kickSubmitDisabled({ isSending })}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 100, backgroundColor: '#EF4444', opacity: isSending ? 0.5 : 1 } as any}
              >
                {isSending ? <ActivityIndicator size="small" color="#FFF" /> : null}
                <Text style={{ color: '#FFF', fontSize: 13, fontWeight: fontWeights.bold, letterSpacing: 0.5 } as any}>ИСКЛЮЧИТЬ</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default KickReasonModal;
